import {
  RecordingMetricKey,
  isUserInteractionMetric,
} from '@spotify-confidence/csr-common';
import type { RecordingRefFragment } from '@spotify/confidence-graphql';

export { RecordingMetricKey, isUserInteractionMetric };

export type RecordingMetricKeyValue =
  (typeof RecordingMetricKey)[keyof typeof RecordingMetricKey];

type MetricDisplay = {
  label: string;
  format: (metric: number | undefined) => string;
  frustrationOrError?: boolean;
};

type MetricDisplayMap = Record<RecordingMetricKeyValue, MetricDisplay> &
  Partial<Record<string, MetricDisplay>>;

export const getMetric = (
  metrics: RecordingRefFragment['metrics'] | undefined,
  key: RecordingMetricKeyValue,
): number | undefined => {
  const value = metrics?.find(m => m.key === key)?.value?.value;
  return value ?? undefined;
};

export const formatMetric = (metric: number | undefined): string =>
  metric === undefined ? '-' : String(metric);

type SignalOption = { id: RecordingMetricKeyValue; label: string };

export const METRIC_DISPLAY: MetricDisplayMap = {
  [RecordingMetricKey.Click]: { label: 'Clicks', format: formatMetric },
  [RecordingMetricKey.Input]: { label: 'Inputs', format: formatMetric },
  [RecordingMetricKey.RageClick]: {
    label: 'Rage clicks',
    format: formatMetric,
    frustrationOrError: true,
  },
  [RecordingMetricKey.FormFieldReEdit]: {
    label: 'Form field re-edits',
    format: formatMetric,
  },
  [RecordingMetricKey.DeadClick]: {
    label: 'Dead clicks',
    format: formatMetric,
    frustrationOrError: true,
  },
  [RecordingMetricKey.ScrollBack]: {
    label: 'Scroll backs',
    format: formatMetric,
  },
  [RecordingMetricKey.TabUnfocus]: {
    label: 'Tab unfocuses',
    format: formatMetric,
  },
  [RecordingMetricKey.ErrorMessage]: {
    label: 'Error messages',
    format: formatMetric,
    frustrationOrError: true,
  },
  [RecordingMetricKey.FailedNetworkRequest]: {
    label: 'Failed requests',
    format: formatMetric,
    frustrationOrError: true,
  },
  [RecordingMetricKey.NetworkRequest]: {
    label: 'Network requests',
    format: formatMetric,
  },
  [RecordingMetricKey.ConsoleLog]: {
    label: 'Console logs',
    format: formatMetric,
  },
  [RecordingMetricKey.ConsoleWarning]: {
    label: 'Console warnings',
    format: formatMetric,
  },
  [RecordingMetricKey.ConsoleError]: {
    label: 'Console errors',
    format: formatMetric,
    frustrationOrError: true,
  },
  [RecordingMetricKey.RouteChange]: {
    label: 'Route changes',
    format: formatMetric,
  },
};

export const SIGNAL_IDS: RecordingMetricKeyValue[] =
  Object.values(RecordingMetricKey);

export const getSignalLabel = (id: string): string =>
  METRIC_DISPLAY[id]?.label ?? id;

export const isFrustrationOrErrorSignal = (id: string): boolean =>
  METRIC_DISPLAY[id]?.frustrationOrError === true;

export const FRUSTRATION_OR_ERROR_SIGNAL_IDS: RecordingMetricKeyValue[] =
  SIGNAL_IDS.filter(isFrustrationOrErrorSignal);

export const SIGNAL_OPTIONS: SignalOption[] = SIGNAL_IDS.map(id => ({
  id,
  label: getSignalLabel(id),
}));
